import { useTranslation } from '@shared/core/i18n';
import { useMemo } from 'react';
import { SearchParams } from '@apps/guest/routes/HotelBooking/HotelBooking.routes';

type RouteBuilder = Readonly<{
  buildRelativeUrl: (eventHandle: string, searchParams: SearchParams) => string;
}>;

export type ScheduleListTranslations = Readonly<{
  addToCalendar: string;
  whereToStay: string;
  addToCal: string;
  dressCode: string;
  getDirections: string;
  moreDetails: string;
  timeframe: (start?: string, end?: string) => string | undefined;
  timeframeTz: (start?: string, end?: string, timezone?: string) => string | undefined;
  timeTz: (time?: string, timezone?: string) => string | undefined;
  description: (
    bookYourStay: RouteBuilder,
    description?: string | null,
    dressCode?: string | null,
    eventHandle?: string | null,
    clientUri?: string | null,
    includeAccommodationsLink?: boolean,
  ) => string | undefined;
  toBeAnnounced: string;
  toBeAnnouncedDescription: string;
}>;

export const useTranslations = (): ScheduleListTranslations => {
  const { t, tq } = useTranslation('guestKit');

  const translations = t('common');

  const value = useMemo<ScheduleListTranslations>(() => {
    return {
      addToCalendar: translations.addToCalendar(),
      whereToStay: translations.whereToStay(),
      addToCal: translations.addToCal(),
      dressCode: translations.dressCode(),
      getDirections: translations.getDirections(),
      moreDetails: translations.moreDetails(),
      timeframe: (start, end) => {
        return start && end ? translations.timeframe({ start, end }) : start || end;
      },
      description: (bookYourStay, description, dressCode, eventHandle, clientUri, includeAccommodationsLink = true) => {
        const contentParts: string[] = [];
        if (description) contentParts.push(description);
        if (dressCode) contentParts.push(`${translations.dressCode()}: ${dressCode}`);

        if (!eventHandle || !clientUri) {
          return contentParts.length > 0 ? tq`${contentParts.join('\n\n')}` : undefined;
        }

        const normalizedClientUri = clientUri.endsWith('/') ? clientUri.slice(0, -1) : clientUri;
        const scheduleUrl = new URL(`${normalizedClientUri}/${eventHandle}/schedule`);
        scheduleUrl.searchParams.set('utm_medium', 'calendar');
        scheduleUrl.searchParams.set('utm_campaign', 'schedule_item');
        scheduleUrl.searchParams.set('utm_source', 'schedule_list_item');

        const linkParts: string[] = [`${translations.moreDetails()}:\n${scheduleUrl.toString()}`];

        if (includeAccommodationsLink) {
          const accommodationsUrl =
            normalizedClientUri +
            bookYourStay.buildRelativeUrl(eventHandle, {
              utm_medium: 'calendar',
              utm_campaign: 'schedule_item',
              utm_source: 'schedule_list_item',
            });
          linkParts.push(`${translations.whereToStay()}:\n${accommodationsUrl}`);
        }

        const allParts = [...contentParts, linkParts.join('\n\n')];
        return tq`${allParts.join('\n\n')}`;
      },
      timeframeTz: (start, end, timezone) => {
        return start && end ? translations.timeframeTz({ start, end, timezone }) : translations.timeTz({ time: start, timezone }) || translations.timeTz({ time: end, timezone });
      },
      timeTz: (time, timezone) => translations.timeTz({ time, timezone }),
      toBeAnnounced: translations.toBeAnnouncedHeader(),
      toBeAnnouncedDescription: translations.toBeAnnouncedDescription(),
    };
  }, [translations, tq]);

  return value;
};
